import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Checkbox, Form, InputNumber, Modal, Space, Switch, Table, message } from "antd";
import { useState } from "react";

import type { SettingRow } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import type { HelpUsage } from "./chatTypes";
import { HelpNoticeDetails } from "./HelpNoticeDetails";
import { useHelpStatus } from "./useHelpChat";

type HelpSettings = { enabled: boolean; acked_notice_version: number; daily_cap_per_user: number;
  daily_cap_total: number; daily_budget_usd: number; monthly_budget_usd: number; usage_retention_days: number };
const fields = [
  ["daily_cap_per_user", "1人の日次回数", 0, 500], ["daily_cap_total", "全体の日次回数", 0, 100000],
  ["daily_budget_usd", "日次予算(USD)", 0, 10000], ["monthly_budget_usd", "月次予算(USD)", 0, 100000],
  ["usage_retention_days", "使用量の保持日数", 30, 730],
] as const;

export function HelpSettingsCard() {
  const api = useApi();
  const client = useQueryClient();
  const status = useHelpStatus();
  const setting = useQuery({ queryKey: ["help-settings"], queryFn: () => api.request<SettingRow>("/api/core/settings/help") });
  const usage = useQuery({ queryKey: ["help-usage"], queryFn: () => api.request<HelpUsage>("/api/help/usage"),
    enabled: !!status.data && status.data.reason !== "feature_disabled" });
  const [consent, setConsent] = useState(false);
  const [ack, setAck] = useState(false);
  const [changed, setChanged] = useState<Partial<HelpSettings>>({});
  const values = { ...(setting.data?.value as HelpSettings | undefined), ...changed };
  const save = useMutation({ mutationFn: (data: Record<string, unknown>) => api.saveSetting("help", data),
    onSuccess: () => { setConsent(false); setAck(false); setChanged({}); void message.success("保存しました");
      for (const key of ["help-settings", "help-status", "help-usage", "settings", "audit"]) void client.invalidateQueries({ queryKey: [key] }); },
    onError: (error: Error) => { void message.error(error.message); } });
  const admin = status.data?.admin;
  return <Card title="ヘルプチャット" style={{ marginBottom: 16 }}>
    <p>{status.data?.provider ?? "—"} / {status.data?.model ?? "—"} — {status.data?.message ?? "利用可能"}</p>
    <p>プロバイダ・接続キーは .env で設定します。</p>
    {!!admin?.manual_problems.length && <Alert type="warning" message="マニュアルの問題"
      description={admin.manual_problems.map((p) => `${p.node}: ${p.problem}`).join("、")} />}
    {admin?.sweep_warning && <Alert type="warning" message="清掃ジョブの最終成功から1時間以上経っています" />}
    <Space>有効にする <Switch checked={!!values.enabled} disabled={!setting.data || save.isPending}
      onChange={(enabled) => { if (enabled) { setAck(false); setConsent(true); } else save.mutate({ enabled: false }); }} /></Space>
    {status.data?.reason === "notice_changed" && <Button onClick={() => { setAck(false); setConsent(true); }}>送信内容を再確認</Button>}
    <Form layout="vertical" style={{ marginTop: 16 }}>
      {fields.map(([key, label, min, max]) => <Form.Item key={key} label={label}>
        <InputNumber aria-label={label} min={min} max={max} precision={key.includes("budget") ? 6 : 0}
          value={values[key]} onChange={(value) => { if (value !== null) setChanged((s) => ({ ...s, [key]: value })); }} />
      </Form.Item>)}
      <Button disabled={!Object.keys(changed).length} loading={save.isPending} onClick={() => save.mutate(changed)}>上限を保存</Button>
    </Form>
    <p>確定費用 今日 ${admin?.spent_today_usd ?? "—"} / 今月 ${admin?.spent_month_usd ?? "—"}。
      未確定 ${admin?.unsettled_usd ?? "—"}。予約超過 {admin?.over_reserved_count ?? 0} 件。</p>
    <p>清掃ジョブの最終成功: {admin?.last_sweep_at ?? "まだありません"}</p>
    <p>直近30日: {usage.data?.totals.requests ?? 0} 件 / 確定費用 ${usage.data?.totals.cost_usd ?? "0"} /
      不明 {usage.data?.totals.unknown_cost_requests ?? 0} 件 / キャッシュ読み率 {usage.data?.totals.cache_read_ratio == null ? "—" :
        `${(usage.data.totals.cache_read_ratio * 100).toFixed(1)}%`}</p>
    <Table scroll={{ x: "max-content" }} size="small" rowKey="date" dataSource={usage.data?.days ?? []} pagination={false}
      columns={[{ title: "日(JST)", width: 120, dataIndex: "date" }, { title: "件数", width: 90, dataIndex: "requests" },
        { title: "確定費用(USD)", width: 180, dataIndex: "cost_usd" }, { title: "未確定(USD)", width: 180, dataIndex: "reserved_unsettled_usd" },
        { title: "不明件数", width: 90, dataIndex: "unknown_cost_requests" }]} />
    {usage.isError && <Alert type="warning" message="使用量を取得できませんでした" />}
    <Modal open={consent} title="外部送信の確認" onCancel={() => setConsent(false)}
      okText="確認して有効にする" okButtonProps={{ disabled: !ack || !status.data }} confirmLoading={save.isPending}
      onOk={() => save.mutate({ enabled: true, notice_ack: status.data?.notice_version })}>
      <HelpNoticeDetails status={status.data} />
      <Checkbox checked={ack} onChange={(e) => setAck(e.target.checked)}>外部送信の内容を確認した</Checkbox>
    </Modal>
  </Card>;
}
